import { fetchSharePointTree, type SPTreeNode } from "./sharePointTree";

/**
 * Find a file inside a project's SharePoint drive by exact file name.
 *
 * We walk the drive tree (folder children, recursively) instead of using
 * Graph's `search(q=...)` action. Search is fuzzy by design and unreliable
 * for files whose names start with numeric codes ("1.3.1 Power Purchase
 * Agreement.pdf") — Graph tokenizes those weirdly and may not return them
 * even when they exist. Listing children deterministically returns
 * everything that's actually in the drive.
 *
 * The tree is cached per (driveId, rootItemId) for the lifetime of the
 * page. First call for a project pays one tree fetch (~30s for 100
 * folders); subsequent lookups are instant. Reload the page to refresh.
 */

const treeCache = new Map<string, SPTreeNode>();
const inflight = new Map<string, Promise<SPTreeNode>>();

export type ResolvedSharePointFile = {
  driveId: string;
  itemId: string;
  webUrl: string;
  size?: number;
};

function cacheKey(driveId: string, rootItemId: string): string {
  return `${driveId}::${rootItemId}`;
}

export async function searchSharePointForFile(
  driveId: string,
  rootItemId: string,
  fileName: string,
): Promise<ResolvedSharePointFile | null> {
  if (!fileName) return null;
  const key = cacheKey(driveId, rootItemId);

  let tree = treeCache.get(key);
  if (!tree) {
    // De-dupe concurrent callers (multiple FDD items resolving in
    // parallel) so we only do one tree fetch per project.
    let pending = inflight.get(key);
    if (!pending) {
      pending = fetchSharePointTree(driveId, rootItemId, undefined, true).then((t) => {
        treeCache.set(key, t);
        return t;
      }).finally(() => {
        inflight.delete(key);
      });
      inflight.set(key, pending);
    }
    tree = await pending;
  }

  return findInTree(tree, fileName, driveId);
}

/**
 * Force the next call to refetch the tree (e.g., after an upload added new
 * files in this session). Cheap to call — the actual fetch only runs when
 * `searchSharePointForFile` is next invoked.
 */
export function invalidateSharePointTreeCache(driveId: string, rootItemId: string): void {
  treeCache.delete(cacheKey(driveId, rootItemId));
}

function findInTree(
  node: SPTreeNode,
  fileName: string,
  driveId: string,
): ResolvedSharePointFile | null {
  const target = fileName.toLowerCase();
  for (const f of node.files ?? []) {
    if (typeof f.name === "string" && f.name.toLowerCase() === target && f.id) {
      return {
        driveId,
        itemId: f.id,
        webUrl: f.webUrl ?? "",
      };
    }
  }
  for (const child of node.children) {
    const r = findInTree(child, fileName, driveId);
    if (r) return r;
  }
  return null;
}
